import * as m from '@staiter/i18n';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '~/components/data';
import { CounterpartyPicker } from '~/components/domain';
import { CompanyContextBanner } from '~/components/domain/CompanyContextBanner';
import { Button, DatePicker, Input, Select, Textarea } from '~/components/primitives';
import { type PaymentCreateBody, createPayment } from '~/lib/api/payments';
import shared from './_auth._shared.module.css';

/**
 * S14-FE.7 — Payment create page.
 * Hotfix 2026-05-23: FE-131 (duplicate Контрагент label was paste-bug on notes_ru
 * field), FE-132 (native HTML5 date → DatePicker), FE-133 (English «External ref»
 * → i18n), FE-134 (Контрагент textbox → counterparty Select), FE-135 (sticky
 * footer + Cancel button).
 */

export const Route = createFileRoute('/_auth/payments/new')({ component: PaymentNewPage });

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function PaymentNewPage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<PaymentCreateBody>({
    direction: 'inflow',
    method: 'bank_transfer',
    counterparty_id: '',
    payment_date: today(),
    amount: { amount: '0', currency: 'KZT' },
    bank_account_ref: null,
    external_ref: null,
    notes_ru: null,
  });

  const createMut = useMutation({
    mutationFn: () => createPayment(form),
    onSuccess: (r) => {
      void navigate({ to: '/payments/$id', params: { id: r.id } });
    },
  });

  return (
    <div>
      <PageHeader title={m.payment_button_new()} />
      <CompanyContextBanner />
      <div className={shared.formGrid2}>
        {/* FE-134 Sprint Q+: CounterpartyPicker primitive (search through full DB). */}
        <CounterpartyPicker
          label={m.payment_column_counterparty()}
          required
          value={form.counterparty_id}
          onChange={(id) => setForm({ ...form, counterparty_id: id })}
        />
        <DatePicker
          label={m.payment_column_date()}
          required
          value={form.payment_date}
          onChange={(next) => setForm({ ...form, payment_date: next ?? today() })}
        />
        <Select
          label={m.payment_column_direction()}
          value={form.direction}
          onChange={(e) => setForm({ ...form, direction: e.target.value as 'inflow' | 'outflow' })}
          options={[
            { value: 'inflow', label: m.payment_direction_inflow() },
            { value: 'outflow', label: m.payment_direction_outflow() },
          ]}
        />
        <Select
          label={m.payment_column_method()}
          value={form.method}
          onChange={(e) =>
            setForm({ ...form, method: e.target.value as PaymentCreateBody['method'] })
          }
          options={[
            { value: 'bank_transfer', label: m.payment_method_bank_transfer() },
            { value: 'cash', label: m.payment_method_cash() },
            { value: 'card', label: m.payment_method_card() },
            { value: 'offset', label: m.payment_method_offset() },
            { value: 'other', label: m.payment_method_other() },
          ]}
        />
        <Input
          label={m.payment_column_amount()}
          required
          inputMode="decimal"
          value={form.amount.amount}
          onChange={(e) => setForm({ ...form, amount: { ...form.amount, amount: e.target.value } })}
        />
        <Select
          label={m.cp_column_currency()}
          value={form.amount.currency}
          onChange={(e) =>
            setForm({
              ...form,
              amount: {
                ...form.amount,
                currency: e.target.value as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY',
              },
            })
          }
          options={[
            { value: 'KZT', label: 'KZT' },
            { value: 'USD', label: 'USD' },
            { value: 'EUR', label: 'EUR' },
            { value: 'RUB', label: 'RUB' },
            { value: 'CNY', label: 'CNY' },
          ]}
        />
        <Input
          label={m.payment_external_ref_label()}
          hint={m.payment_external_ref_hint()}
          value={form.external_ref ?? ''}
          onChange={(e) => setForm({ ...form, external_ref: e.target.value || null })}
        />
        <Textarea
          label={m.payment_notes_label()}
          placeholder={m.payment_notes_placeholder()}
          rows={3}
          value={form.notes_ru ?? ''}
          onChange={(e) => setForm({ ...form, notes_ru: e.target.value || null })}
        />
      </div>

      <div className={shared.stickyFooter}>
        <Button type="button" variant="secondary" onClick={() => navigate({ to: '/payments' })}>
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => createMut.mutate()}
          loading={createMut.isPending}
          disabled={!form.counterparty_id || !form.amount.amount}
        >
          {m.common_button_create()}
        </Button>
      </div>
    </div>
  );
}
